import React, { useState, useEffect } from "react";
import { API } from "../../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const TYPES = [
  { value: "phone", label: "Phone" },
  { value: "on_site", label: "On Site" },
  { value: "meeting", label: "Meeting" },
  { value: "email", label: "Email" },
  { value: "other", label: "Other" },
];

const today = () => new Date().toISOString().slice(0, 10);

const empty = () => ({
  client_name: "",
  client_email: "",
  client_phone: "",
  date: today(),
  type: "phone",
  subject: "",
  summary: "",
});

export function ConversationFormDialog({ open, onClose, siteId, editRecord, defaultClient, onSaved }) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const token = () => localStorage.getItem("token");

  useEffect(() => {
    if (open) {
      setForm(
        editRecord
          ? {
              client_name: editRecord.client_name || "",
              client_email: editRecord.client_email || "",
              client_phone: editRecord.client_phone || "",
              date: editRecord.date || today(),
              type: editRecord.type || "phone",
              subject: editRecord.subject || "",
              summary: editRecord.summary || "",
            }
          : {
              ...empty(),
              client_name: defaultClient?.client_name || "",
              client_email: defaultClient?.client_email || "",
              client_phone: defaultClient?.client_phone || "",
            }
      );
    }
  }, [open, editRecord, defaultClient]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.client_name.trim() || !form.client_email.trim()) {
      toast.error("Client name and email are required");
      return;
    }
    setSaving(true);
    try {
      const url = editRecord
        ? `${API}/client-comms/${editRecord.id}`
        : `${API}/client-comms`;
      const method = editRecord ? "PUT" : "POST";
      const body = editRecord ? form : { ...form, site_id: siteId };
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Save failed");
      }
      toast.success(editRecord ? "Conversation updated" : "Conversation logged");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="conversation-form-dialog">
        <DialogHeader>
          <DialogTitle>{editRecord ? "Edit Conversation" : "Log Conversation"}</DialogTitle>
          <DialogDescription>
            Saved as a draft — the client only sees it once you send the link.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client name *</Label>
              <Input
                value={form.client_name}
                onChange={(e) => set("client_name", e.target.value)}
                data-testid="conversation-client-name"
              />
            </div>
            <div>
              <Label>Client email *</Label>
              <Input
                type="email"
                value={form.client_email}
                onChange={(e) => set("client_email", e.target.value)}
                data-testid="conversation-client-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client phone</Label>
              <Input
                value={form.client_phone}
                onChange={(e) => set("client_phone", e.target.value)}
                data-testid="conversation-client-phone"
              />
            </div>
            <div>
              <Label>Date of conversation</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                data-testid="conversation-date"
              />
            </div>
          </div>

          <div>
            <Label>Type</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set("type", t.value)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    form.type === t.value
                      ? "bg-amber-600 text-white border-amber-600"
                      : "bg-white text-slate-600 border-slate-300 hover:border-amber-400"
                  }`}
                  data-testid={`conversation-type-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => set("subject", e.target.value)}
              placeholder="e.g. Tile selection discussion"
              data-testid="conversation-subject"
            />
          </div>

          <div>
            <Label>Summary</Label>
            <Textarea
              rows={5}
              value={form.summary}
              onChange={(e) => set("summary", e.target.value)}
              placeholder="What was discussed and any decisions made"
              data-testid="conversation-summary"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="conversation-save-btn">
            {saving ? "Saving..." : "Save as Draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
